// Linguagem visual própria do módulo de Cronogramas (estilo "monday").
// Centraliza paleta de status, gradientes de avatar e helpers de derivação.

export const ACTIVITY_STATUS_OPTIONS = [
 { value: 'NAO_INICIADO', label: 'Não iniciado' },
 { value: 'EM_ANDAMENTO', label: 'Em andamento' },
 { value: 'IMPEDIDA', label: 'Impedida' },
 { value: 'CONCLUIDO', label: 'Concluído' },
 { value: 'CANCELADA', label: 'Cancelada' },
];

export const SCHEDULE_STATUS_OPTIONS = [
 { value: 'NAO_INICIADO', label: 'Não iniciado' },
 { value: 'EM_ANDAMENTO', label: 'Em andamento' },
 { value: 'CONCLUIDO', label: 'Concluído' },
];

export const ACTIVITY_STATUS_LABEL = ACTIVITY_STATUS_OPTIONS.reduce(
 (acc, o) => ({ ...acc, [o.value]: o.label }),
 {},
);

export const SCHEDULE_STATUS_LABEL = SCHEDULE_STATUS_OPTIONS.reduce(
 (acc, o) => ({ ...acc, [o.value]: o.label }),
 {},
);

// Paleta monday: cada status tem cor sólida (chip/board), fundo suave (coluna)
// e cor de texto legível sobre a cor sólida.
export const STATUS_META = {
 NAO_INICIADO: { label: 'Não iniciado', solid: '#9AA5B1', soft: '#EEF1F5', on: '#FFFFFF' },
 EM_ANDAMENTO: { label: 'Em andamento', solid: '#FDAB3D', soft: '#FFF4E1', on: '#5A3A00' },
 IMPEDIDA: { label: 'Impedida', solid: '#E2445C', soft: '#FDE8EC', on: '#FFFFFF' },
 CONCLUIDO: { label: 'Concluído', solid: '#00C875', soft: '#E2F8EF', on: '#FFFFFF' },
 CANCELADA: { label: 'Cancelada', solid: '#7A8290', soft: '#EDEFF2', on: '#FFFFFF' },
};

// Ordem das colunas do board de atividades.
export const ACTIVITY_COLUMNS = [
 'NAO_INICIADO',
 'EM_ANDAMENTO',
 'IMPEDIDA',
 'CONCLUIDO',
 'CANCELADA',
];

const TERMINAL = ['CONCLUIDO', 'CANCELADA'];

// Compat com telas antigas (badge Chakra colorScheme).
export const STATUS_COLOR = {
 NAO_INICIADO: 'gray',
 EM_ANDAMENTO: 'orange',
 IMPEDIDA: 'red',
 CONCLUIDO: 'green',
 CANCELADA: 'gray',
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.NAO_INICIADO;

// Identidade do sistema: azul de marca (acentos/CTAs) e laranja (ação/realce).
export const BLUE = '#0173BC';
export const BLUE_STRONG = '#0062FF';
export const ORANGE = '#FF6700';
export const ACCENT = BLUE;

// Cores sólidas e terrosas p/ avatares (determinístico por nome) — sem azul/roxo.
const AVATAR_COLORS = ['#C2453D', '#CF7A2C', '#3E8E6E', '#5A636E', '#A06A37', '#6E8A3C'];

export function avatarColor(seed = '') {
 let h = 0;
 for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
 return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function initials(name = '') {
 const parts = String(name).trim().split(/\s+/).filter(Boolean);
 if (parts.length === 0) return '?';
 if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
 return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Estado de prazo: 'overdue' | 'today' | null (terminal/sem data → null).
export function dueState(endDate, status) {
 if (!endDate || TERMINAL.includes(status)) return null;
 const today = new Date().toISOString().slice(0, 10);
 const end = String(endDate).slice(0, 10);
 if (end < today) return 'overdue';
 if (end === today) return 'today';
 return null;
}

// Progresso do cronograma = atividades concluídas / total (canceladas fora).
export function progressOf(activities = []) {
 const live = activities.filter((a) => a.status !== 'CANCELADA');
 const total = live.length;
 const done = live.filter((a) => a.status === 'CONCLUIDO').length;
 const pct = total ? Math.round((done / total) * 100) : 0;
 return { done, total, pct };
}

export function countOverdue(activities = []) {
 return activities.filter((a) => dueState(a.end_date, a.status) === 'overdue').length;
}

// Quebra de progresso p/ a barra segmentada (concluído / em andamento / impedida).
// Base = atividades vivas (exclui canceladas).
export function progressBreakdown(activities = []) {
 const live = activities.filter((a) => a.status !== 'CANCELADA');
 const total = live.length;
 const done = live.filter((a) => a.status === 'CONCLUIDO').length;
 const doing = live.filter((a) => a.status === 'EM_ANDAMENTO').length;
 const blocked = live.filter((a) => a.status === 'IMPEDIDA').length;
 const pct = total ? Math.round((done / total) * 100) : 0;
 return { total, done, doing, blocked, pct };
}

// Dias até a data de fim (negativo = já passou); null se sem data. Sem fuso.
export function daysUntil(endDate) {
 if (!endDate) return null;
 const [y, m, d] = String(endDate).slice(0, 10).split('-').map(Number);
 if (!y || !m || !d) return null;
 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const end = new Date(y, m - 1, d);
 return Math.round((end.getTime() - today.getTime()) / 86400000);
}

// Cor determinística para a tag de Fase (bg suave + texto forte).
const FASE_PALETTE = [
 { bg: '#EAF2FB', color: '#2B6CB0' },
 { bg: '#F3ECFB', color: '#7C4DBB' },
 { bg: '#E6F6F4', color: '#1F8A7D' },
 { bg: '#FDF3E2', color: '#A86A00' },
 { bg: '#E9F6F0', color: '#2F7D58' },
 { bg: '#FDEBEF', color: '#C0395A' },
];

export function faseColor(seed = '') {
 let h = 0;
 for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
 return FASE_PALETTE[h % FASE_PALETTE.length];
}

// Datas chegam como 'YYYY-MM-DD' (ou ISO). Formata p/ dd/mm/aaaa sem fuso.
export function formatDate(value) {
 if (!value) return '-';
 const [year, month, day] = String(value).slice(0, 10).split('-');
 if (!year || !month || !day) return '-';
 return `${day}/${month}/${year}`;
}
